import SkeletonBox from 'components/generic/SkeletonBox';
import { useSavedSearch } from 'components/SavedSearch/SavedSearchContextProvider';
import { qsOptions } from 'providers/clients/httpClient';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useCosmosRuntime } from 'providers/contexts/CosmosRuntimeContext';
import { SavedSearchItem } from 'providers/contexts/extensions/SavedSearches';
import qs from 'qs';
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRecordContext, useResourceContext } from 'react-admin';
import {
  NavigateOptions,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { mergeDeep } from 'remeda';
import { WireSearchContext } from 'screens/syndication/views/search/syndication/WireSearchWithContext';
import {
  getRoute,
  isAssetTypeEditorEnabled,
  removeNullOrUndefinedValues,
} from 'utility/functions';
import { isAdminManagementRoute } from 'utility/adminRouteUtils';
import { hasSubscriptionEntitlement } from 'utility/privileges';
import { AnyRecord, UseLocationType } from 'utility/types/cosmosTypes';
import { processValidationResult, validateZodObject } from 'utility/zodFunctions';
import { SearchAdvancedContextProvider } from './components/SearchAdvance/SearchAdvancedContextProvider';
import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import { getRecordRealId } from './components/SearchItemsProvider';
import { preservedKeys } from './const';
import {
  buildSavedSearchParams,
  createSearchZodValidator,
  getKnownSearchQueryParams,
  getPreferenceKey,
  getSortableOptions,
  getViewKeyLS,
  TResourceContexts,
} from './functions';
import { useDefaultSearch } from './hooks';
import { GetSearchItemInfoResponse, NavApplyQpParams, SearchContextType } from './types';
import { DASHBOARD_RESOURCE } from 'screens/dashboard/custom-dashboards/utils/constants';
import { getDashboardLink } from 'screens/dashboard/custom-dashboards/utils/functions';
import { useEditorialAiSearchState } from './components/SearchAdvance/components/AISearch/useEditorialAiSearchState';

/**
 * ---------- SEARCH CONTEXT PROVIDER ----------
 */
export const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within an SearchContextProvider');
  }
  return context;
};

/**
 * Safe version of useSearch that returns null instead of throwing
 * when used outside of SearchContextProvider. Useful for components
 * that may optionally be used within or outside search views.
 */
export const useSearchSafe = (): SearchContextType | null => {
  return useContext(SearchContext) ?? null;
};

interface SearchContextProviderProps {
  dashboard?: string;
  children?: React.ReactNode;
}

type AnyRecordObj = {
  [key: string]: any;
};

/**
 * Utility to parse query params from location.search.
 * Returns parsed params object or empty object if no params.
 * Uses qs library to parse params.
 * eg: '?previews[size1]=550w' -> { previews: { size1:'550w' } }
 *
 * param: queryStr - query string to parse
 * eg: location.search / '?foo=bar&baz=qux'
 */
type ParseQueryStringArgs = {
  queryStr: string | undefined;
};

function parseQueryString({ queryStr }: ParseQueryStringArgs) {
  try {
    // no string provided
    if (!queryStr) {
      return {};
    }

    const parsed = qs.parse(queryStr, {
      ignoreQueryPrefix: true,
      throwOnLimitExceeded: true,
    });

    const result = parsed || {};

    return result;
  } catch (err) {
    console.error('[parseQueryParams]:', err);
    return {};
  }
}

export type BuildQueryUrlArgs = {
  baseUrl: string;
  newParams?: AnyRecordObj;
};

/**
 * Utility to build query params for a given base URL.
 * Returns the full url including qp or base URL if no params.
 *
 * Certain search resources will build final URLs to
 * add/modify qp for items in search, for example certain
 * resources open modals on top of existing search
 * when the rows/items are clicked.
 *
 * Uses qs library to stringify params.
 * eg: { previews: { size1:'550w' } } -> 'previews[size1]': '550w'
 */

export function buildQueryUrl({ baseUrl, newParams }: BuildQueryUrlArgs): string {
  try {
    if (!baseUrl) {
      throw new Error('[buildQueryParams] No baseUrl provided.');
    }

    const qpObj = newParams || {};
    const hasParams = Object.keys(qpObj).length > 0;
    const qpStr = hasParams ? qs.stringify(qpObj, qsOptions) : '';

    // Add qs to baseurl if present or return base url
    const finalUrl = qpStr ? `${baseUrl}?${qpStr}` : baseUrl;

    return finalUrl;
  } catch (err) {
    console.error('[buildQueryParams]:', err);
    return '';
  }
}

type GetSearchItemInfoParams = {
  record: AnyRecord;
  resource?: string;
  activeSite: string;
  location?: UseLocationType; // only used by some resources
  sessionConfig?: TSessionConfigData;
};

/**
 * Get the app string from a saved search
 * record, for routing in the application.
 *
 * eg: wire is renamed to 'feeds' in the route
 * other apps like editorial remain the same.
 *
 */
function getSavedSearchAppRoute(record: SavedSearchItem): string {
  const app = record?.app;
  switch (app) {
    case 'wire': {
      return 'feeds';
    }
    default: {
      return app || '';
    }
  }
}

/**
 * Get search item info based on the resource and record.
 * Returns an object with: url, target, disabled and
 * possibly any other info that the row/search item
 * needs to determine how to render the link/button.
 *
 */
export function getSearchItemInfoBase({
  record,
  resource,
  activeSite,
  location,
  sessionConfig,
}: GetSearchItemInfoParams): GetSearchItemInfoResponse {
  if (!record) {
    return {
      url: '',
      target: '_self',
      disabled: true,
    };
  }

  // handle specific resources
  switch (resource) {
    // Handle saved search resource
    case 'savedsearch': {
      // casting for typehints
      const rec = record as SavedSearchItem;
      if (!rec?.payload || !rec?.app) {
        console.warn('[getSearchItemInfo] Missing payload/app:', record);
      }

      // get the client side app route string
      const appRoute = getSavedSearchAppRoute(rec);

      /**
       * build the search params from the saved search payload,
       * add 'quickFilterId' (QUICKFILTER_ID_QP) query param
       * to the url so  that the quick filter selector can fetch
       * the saved search by id and apply it to the state of
       * the input value and the URL, if applicable.
       */

      // add quickFilterId query param if id is present
      const extraParams = rec?.id ? { quickFilterId: rec?.id } : {};

      const searchParams = buildSavedSearchParams({
        payload: rec?.payload,
        addParams: extraParams,
        timeZone: sessionConfig?.time_zone,
      });

      // determine if we can return a routable url
      const isRoutable = Boolean(rec?.payload && appRoute);
      const finalUrl = isRoutable
        ? `/${activeSite}/${appRoute}/search/${searchParams}`
        : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    // Handle search resources
    case 'editorial':
    case 'editorial/templates': {
      const itemId = getRecordRealId(record, resource);
      const canEdit = isAssetTypeEditorEnabled(record?.type);

      const isRoutable = Boolean(itemId && canEdit);
      const finalUrl = isRoutable ? `/${activeSite}/editorial/edit/${itemId}/` : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'user': {
      const itemId = getRecordRealId(record, resource);
      const actionParam = new URLSearchParams(location?.search).get('action');
      const actionRoute = actionParam ? `?action=${actionParam}` : '';

      // Route admin users to /settings/admins/edit, regular users to /user/edit
      const isAdminUser = record?.account_type === 'admin';
      const editPath = isAdminUser ? 'settings/admins/edit' : 'user/edit';

      const isRoutable = Boolean(itemId);
      const finalUrl = isRoutable
        ? `/${activeSite}/${editPath}/${itemId}${actionRoute}`
        : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'user/account/multisite': {
      const itemId = getRecordRealId(record, resource);
      const userDomain = record?.domain;
      if (!userDomain) {
        console.warn('[getSearchItemInfo - multisite] Missing domain:', record);
      }
      const baseOrigin = window.location.origin;
      const isRoutable = Boolean(itemId && userDomain);

      const finalUrl = isRoutable
        ? `${baseOrigin}/${userDomain}/user/edit/${itemId}`
        : '';

      return {
        url: finalUrl,
        target: '_blank',
        disabled: !isRoutable,
      };
    }

    case 'wire/channel/search': {
      const itemId = getRecordRealId(record, resource);
      const channel = new URLSearchParams(location?.search)?.get('channel_id');
      if (!channel) {
        console.warn('[SearchGridRow - wire/channel/search] Missing channel id', record);
      }

      const isDeleted = record?.status === 'deleted';
      const canEdit = !isDeleted && isAssetTypeEditorEnabled(record?.type);

      // encodeURIComponent can throw...
      let uriInfo = '';
      try {
        uriInfo = encodeURIComponent(itemId);
      } catch (error) {
        console.error('[getSearchItemInfo - wire/channel/search] Error:', error);
      }

      const isRoutable = Boolean(channel && itemId && canEdit && uriInfo);
      const finalUrl = isRoutable
        ? `/${activeSite}/feeds/view/${channel}/${uriInfo}/`
        : '';

      // Return the standard feeds view route info
      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }
    case 'wire/channel': {
      const itemId = getRecordRealId(record, resource);
      const isRoutable = Boolean(itemId);
      const finalUrl = `/${activeSite}/feeds/channels/edit/${itemId}/`;

      // Return the standard feeds view route info
      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }
    // Notifier Messages Search (.../notifier/search)
    // Opens edit modal for broadcast/message in search.
    case 'broadcast/message': {
      const itemId = getRecordRealId(record, resource);
      const isRoutable = Boolean(itemId);
      const baseUrl = `/${activeSite}/notifier/search`;

      // This view opens a modal on top of existing search
      // Keep all current query param keys to preserve them

      // Parse all current params
      const currentQP = parseQueryString({ queryStr: location?.search });
      // Keep all current qp and add openModal id param
      const finalParams = mergeDeep(currentQP, { openModal: itemId });
      // Build url using the new merged params
      const builtUrl = buildQueryUrl({ baseUrl: baseUrl, newParams: finalParams });
      // Final routable check
      const finalUrl = isRoutable ? builtUrl : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    // Notifier Channels Search (.../notifier/channels)
    // Opens edit modal for broadcast/channel in search.
    case 'broadcast/channel': {
      const itemId = getRecordRealId(record, resource);
      const isRoutable = Boolean(itemId);
      const baseUrl = `/${activeSite}/notifier/channels`;

      // This view opens a modal on top of existing search
      // Parse all current params
      const currentQP = parseQueryString({ queryStr: location?.search });
      // Keep all current qp and add openModal id param
      const finalParams = mergeDeep(currentQP, { openModal: itemId });
      // Build url using the new merged params
      const builtUrl = buildQueryUrl({ baseUrl: baseUrl, newParams: finalParams });
      // Final routable check
      const finalUrl = isRoutable ? builtUrl : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'emailreach/account': {
      const itemId = getRecordRealId(record, resource);
      const isRoutable = Boolean(itemId);
      const baseUrl = `/${activeSite}/emailreach/account/search`;

      // Opens a modal on top of existing search
      // Parse current qp, add openModal and build final url
      const currentQP = parseQueryString({ queryStr: location?.search });
      const finalParams = mergeDeep(currentQP, { openModal: itemId });
      const builtUrl = buildQueryUrl({ baseUrl: baseUrl, newParams: finalParams });

      const finalUrl = isRoutable ? builtUrl : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'emailreach/contact': {
      const itemId = getRecordRealId(record, resource);
      const isRoutable = Boolean(itemId);
      const baseUrl = `/${activeSite}/emailreach/contacts/search`;

      // Opens a modal on top of existing search
      // Parse current qp, add openModal and build final url
      const currentQP = parseQueryString({ queryStr: location?.search });
      const finalParams = mergeDeep(currentQP, { openModal: itemId });
      const builtUrl = buildQueryUrl({ baseUrl: baseUrl, newParams: finalParams });

      const finalUrl = isRoutable ? builtUrl : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'emailreach/contactlist': {
      const itemId = getRecordRealId(record, resource);
      const isRoutable = Boolean(itemId);
      const baseUrl = `/${activeSite}/emailreach/lists/search`;

      // Opens a modal on top of existing search
      // Parse current qp, add openModal and build final url
      const currentQP = parseQueryString({ queryStr: location?.search });
      const finalParams = mergeDeep(currentQP, { openModal: itemId });
      const builtUrl = buildQueryUrl({ baseUrl: baseUrl, newParams: finalParams });

      const finalUrl = isRoutable ? builtUrl : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'emailreach/messages': {
      const externalId = record?.external_id || '';
      const hasExternalId = Boolean(externalId);
      const isRoutable = Boolean(hasExternalId);
      const baseUrl = `/${activeSite}/emailreach/messages/search`;

      // Opens a modal on top of existing search
      // Parse current qp, add openModal with external_id (API lookup key)
      const currentQP = parseQueryString({ queryStr: location?.search });
      const finalParams = mergeDeep(currentQP, { openModal: externalId });
      const builtUrl = buildQueryUrl({ baseUrl: baseUrl, newParams: finalParams });

      const finalUrl = isRoutable ? builtUrl : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'emailreach/campaign': {
      const itemId = getRecordRealId(record, resource);
      const isRoutable = Boolean(itemId);
      const baseUrl = `/${activeSite}/emailreach/campaigns/search`;

      // Opens a modal on top of existing search
      // Parse current qp, add openModal and build final url
      const currentQP = parseQueryString({ queryStr: location?.search });
      const finalParams = mergeDeep(currentQP, { openModal: itemId });
      const builtUrl = buildQueryUrl({ baseUrl: baseUrl, newParams: finalParams });

      const finalUrl = isRoutable ? builtUrl : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'subscription/services': {
      const itemId = getRecordRealId(record, resource);
      // Only routable if it's a subscription row, others
      // types will be added in future tickets
      // And then ultimately we can remove this
      // check once all types are handled
      const isRoutable = Boolean(itemId);
      const finalUrl = isRoutable ? `/${activeSite}/services/edit/${itemId}` : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    case 'pay/transactions': {
      const itemId = getRecordRealId(record, resource);
      const isRoutable = Boolean(itemId);
      const finalUrl = isRoutable ? `/${activeSite}/transactions/view/${itemId}` : '';

      return {
        url: finalUrl,
        target: '_self',
        disabled: !isRoutable,
      };
    }

    // Notifier Connections (.../notifier/connections)
    // Accounts are not individually navigable; actions are inline.
    case 'broadcast/account': {
      return {
        url: '',
        target: '_self',
        disabled: false,
      };
    }

    case DASHBOARD_RESOURCE: {
      const finalUrl = getDashboardLink({
        baseUrl: `/${activeSite}`,
        dashboardData: record,
      });

      return {
        url: finalUrl,
        target: '_self',
        disabled: !finalUrl?.length || finalUrl === '#',
      };
    }

    default: {
      // Only warn if resource is defined but not handled - undefined resource
      // is expected in contexts like tests or components outside ResourceContext
      if (resource) {
        console.warn('[getSearchItemInfo] - Defaulted:', { record, resource });
      }
      return {
        url: '',
        disabled: true,
      };
    }
  }
}

/**
 * SearchContextProvider
 */
export const SearchContextProvider = memo(
  ({ dashboard, children }: SearchContextProviderProps) => {
    const resource = useResourceContext();
    const location = useLocation();
    const navigate = useNavigate();

    // Use Specific Contexts Safely + Memoize
    const wireSearchContext = useContext(WireSearchContext);
    const resourceContexts: TResourceContexts = useMemo(() => {
      return { 'wire/channel/search': wireSearchContext };
    }, [wireSearchContext]);

    const { defaultSavedSearchState, updateQuickFilterValue } = useSavedSearch();

    const hasDefaultSavedSearch = Boolean(
      defaultSavedSearchState.initialized &&
      defaultSavedSearchState.hasDefaultSavedSearch,
    );

    const record = useRecordContext<{ data?: any }>()?.data;
    const { sessionConfig, activeSite, userInfo } = useCosmos();
    const { history, lastLocation } = useCosmosRuntime();
    const [queryParams, updateQueryParams] = useSearchParams();

    // Get view from local storage and set preferenceKey for summary or table (list) view
    const [view, setView] = useState(() => getViewKeyLS(resource));
    const [preferenceKey, setPreferenceKey] = useState(() => getPreferenceKey(view));
    const currentRoute = useMemo(() => getRoute(location.pathname), [location.pathname]);
    const kpibarStateStorageKey = useMemo(() => `${currentRoute}-kpibar`, [currentRoute]);

    const { isEditorialAndActive } = useEditorialAiSearchState({
      resource: resource,
      sessionConfig: sessionConfig,
      userInfo: userInfo,
    });

    const defaultSearch = useDefaultSearch({
      resource: resource,
      context: resourceContexts,
      isAiSearchActive: isEditorialAndActive,
    });

    // User Search Specific
    const accountType = useMemo(() => {
      return (
        record?.account_type ||
        (isAdminManagementRoute(currentRoute) ? 'admin' : 'normal')
      );
    }, [record, currentRoute]);

    const siteHasSubEnabled = useMemo(() => {
      return hasSubscriptionEntitlement(sessionConfig);
    }, [sessionConfig]);

    const includeSubInfo = useMemo(() => {
      return accountType === 'normal' && siteHasSubEnabled;
    }, [accountType, siteHasSubEnabled]);

    const queryParamsObject = Object.fromEntries(queryParams.entries());

    const hasNoQueryParams = Object.keys(queryParamsObject).length === 0;

    // User explicitly typed in the address bar eg '/editorial/search'
    const directNavToSearch = history.length === 1 && !lastLocation;

    // User navigated from Bento or LeftNav with state reloadDefault.
    const reloadDefaultSignal = Boolean(
      location?.state?.reloadDefault || directNavToSearch,
    );

    const applySearchQP = Boolean(
      (directNavToSearch || reloadDefaultSignal) && hasNoQueryParams,
    );

    const isSavedSearch = useMemo(() => {
      return resource === 'savedsearch';
    }, [resource]);

    const isWireSearch = useMemo(() => {
      return resource === 'wire/channel/search';
    }, [resource]);

    // Define a stable key for the search context based on pathname
    // If this changes it will remount the data grid and reset state
    // as pathname doesnt include qp, it should be stable
    const searchProviderKey = useMemo(() => {
      const stableKey = location.pathname;
      return stableKey;
    }, [location.pathname]);

    const navigateApplyQueryParams = useCallback(
      ({ params, navOpts }: NavApplyQpParams) => {
        const newParams = new URLSearchParams(params).toString();
        navigate(`${location.pathname}?${newParams}${location.hash}`, navOpts);
        return;
      },
      [navigate, location.pathname, location.hash],
    );

    /**
     * Apply default filter/sort params or default saved search.
     *
     * Handle direct navigation:
     *  - With query params: respect existing query params
     *  - Without query params: apply default savedsearch if exists, else default params
     *
     * Handle reloadDefault signal:
     *  - If true apply default savedsearch if exists, else default params
     *
     * When applying, use navigate and set reload default to false
     * to prevent the effect from re-running again after applying.
     *
     */

    useEffect(() => {
      // [wire exception - todo fix/improve/remove]
      // see WireSearchWithContext for process of
      // loading default query params for search
      if (!resource || isWireSearch) {
        return;
      }

      if (!reloadDefaultSignal || isSavedSearch) {
        return;
      }

      if (applySearchQP) {
        // No default saved search, apply default filter/sort params
        if (!hasDefaultSavedSearch) {
          navigateApplyQueryParams({
            params: defaultSearch,
            navOpts: {
              state: {
                reloadDefault: false, // reset state to prevent loops
              },
              replace: true, // replace history as this autoapply params
            },
          });
          return;
        }

        // we have a default saved search, apply the payload params
        if (hasDefaultSavedSearch) {
          const ssItem = defaultSavedSearchState?.defaultSavedSearch;
          const ssPayload = ssItem?.payload || {};
          const savedSearchParamsString = buildSavedSearchParams({
            payload: ssPayload,
            timeZone: sessionConfig?.time_zone,
          });
          // navigate with the saved search params and also
          // preload quickfilter select value with the ss item
          updateQuickFilterValue(ssItem);
          navigateApplyQueryParams({
            params: savedSearchParamsString,
            navOpts: {
              state: {
                reloadDefault: false,
              },
              replace: true,
            },
          });
          return;
        }
      }
    }, [
      applySearchQP,
      defaultSavedSearchState?.defaultSavedSearch,
      defaultSearch,
      hasDefaultSavedSearch,
      isSavedSearch,
      isWireSearch,
      navigateApplyQueryParams,
      reloadDefaultSignal,
      resource,
      updateQuickFilterValue,
      sessionConfig?.time_zone,
    ]);

    // Helper wrapper to get a nav url, target, etc..
    // and other data (eg disabled state) for search items
    const getSearchItemInfo = useCallback(
      (record: AnyRecord): GetSearchItemInfoResponse => {
        // Get the search item info based on the resource and record
        const info = getSearchItemInfoBase({
          record: record,
          resource: resource,
          activeSite: activeSite,
          location: location,
          sessionConfig: sessionConfig,
        });

        return info;
      },
      [resource, activeSite, location, sessionConfig],
    );

    // swap view helper card summary list
    const swapView = useCallback((newView: SearchContextType['view']) => {
      setView((prevView: SearchContextType['view']) => {
        localStorage.setItem('currentView', newView);
        return newView;
      });
      setPreferenceKey(newView === 'list' ? 'search-table-view' : 'search-summary-view');
    }, []);

    // Get preserved keys for this resource
    const preservedKeysArray = useMemo(() => {
      if (!resource || !preservedKeys || !preservedKeys?.[resource]) {
        return [];
      }
      return preservedKeys?.[resource] || [];
    }, [resource]);

    // Reset query params to only preserved keys
    const resetQpFilters = useCallback(
      (navOpts?: NavigateOptions) => {
        updateQueryParams((prevQueryParams: URLSearchParams) => {
          const newQueryParams = new URLSearchParams(
            Array.from(prevQueryParams).filter(([key]) =>
              preservedKeysArray.includes(key),
            ),
          );
          return newQueryParams;
        }, navOpts);
      },
      [updateQueryParams, preservedKeysArray],
    );

    // clear query params helper
    const clearAllQueryParams = useCallback(
      (navOpts?: NavigateOptions) => {
        updateQueryParams(() => new URLSearchParams(), navOpts);
      },
      [updateQueryParams],
    );

    //-------------------------------
    //---- Query Param Validation ---
    //-------------------------------
    const qpValidationResult = useMemo(() => {
      // Get known query params and remove null or undefined values (for the zod validator)
      // We can also adjust the zod schema to allow null/undef/missing keys if needed.
      const knownQueryParams = getKnownSearchQueryParams({
        queryParams: queryParams,
        resource: resource,
      });
      const knownQPCleaned = removeNullOrUndefinedValues(knownQueryParams);
      const sortOptions = getSortableOptions(resource, resourceContexts);
      // Create the zod validator schema
      const zodValidator = createSearchZodValidator({
        validSortKeys: sortOptions,
        withSub: includeSubInfo,
        resource: resource,
      });
      // Validate the cleaned qps obj with the validator
      const validatedQP = validateZodObject({
        value: knownQPCleaned,
        validator: zodValidator,
      });
      // Process the validation result, will return the validListFilter and/or badKeys array
      const validResult = processValidationResult({ parseResult: validatedQP });
      return validResult;
    }, [queryParams, includeSubInfo, resource, resourceContexts]);

    // Remove bad query param key/values from the URL based on the validation result
    useEffect(() => {
      const badKeys = qpValidationResult?.badKeys || [];
      if (badKeys.length > 0) {
        updateQueryParams((prevQueryParams) => {
          badKeys.forEach((key) => {
            prevQueryParams.delete(key);
          });
          return prevQueryParams;
        });
      }
    }, [qpValidationResult, updateQueryParams]);

    const contextValue: SearchContextType = useMemo(() => {
      const validListFilter = qpValidationResult?.validListFilter || {};

      let extendedValidListFilter = { ...validListFilter };

      if (resource === 'user') {
        extendedValidListFilter = {
          ...extendedValidListFilter,
          account_type: accountType,
        };

        // add the 'subscription' related params only if
        // site has subscription entitlement else even if you pass
        // 'subscription=false' on a site that doesnt have sub entitlement,
        // api will return an error of "Subscription support is not activated"...
        if (siteHasSubEnabled) {
          extendedValidListFilter.subscription = includeSubInfo;
        }
      }

      return {
        // Universal Search
        dashboard,
        validListFilter: extendedValidListFilter,
        queryParams,
        updateQueryParams,
        clearAllQueryParams,
        resetQpFilters,
        view,
        swapView,
        preferenceKey,
        getSearchItemInfo,
        hasNoQueryParams,
        kpibarStateStorageKey,
        preservedKeysArray,
        // User Search Specific
        subEnabled: siteHasSubEnabled,
        accountType,
        // Saved Search / Quick Filter
        isSavedSearch,
        resourceContexts,
      };
    }, [
      accountType,
      clearAllQueryParams,
      dashboard,
      getSearchItemInfo,
      hasNoQueryParams,
      includeSubInfo,
      isSavedSearch,
      kpibarStateStorageKey,
      preferenceKey,
      preservedKeysArray,
      qpValidationResult?.validListFilter,
      queryParams,
      resetQpFilters,
      resource,
      siteHasSubEnabled,
      swapView,
      updateQueryParams,
      view,
      resourceContexts,
    ]);

    // If we are awaiting the first saved default search query loading, show the skeleton loader
    if (!resource || !kpibarStateStorageKey || (applySearchQP && !isSavedSearch)) {
      return (
        <SkeletonBox
          width="100%"
          height="100%"
          text="Loading Search..."
        />
      );
    }

    return (
      <SearchContext.Provider
        key={searchProviderKey}
        value={contextValue}
      >
        <SearchAdvancedContextProvider>{children}</SearchAdvancedContextProvider>
      </SearchContext.Provider>
    );
  },
);
SearchContextProvider.displayName = 'SearchContextProvider';
